import React from "react";
import { Commend3Model, Commend3SortOrder } from "../Types";
import { Commend3ClientSortField } from "../Commend3Facets";
import { MODELS_TABLE_MIN_WIDTH, NOT_CONFIGURED_LABEL } from "../Commend3Constants";
import {
  formatDateOnly,
  formatTimestampToUTC,
  getStrategyLabel,
  getUpdatedByName,
  getWidgetLabel,
  hasStrategy,
  resolvePromotionBadge,
} from "../Commend3Helper";
import RowActionsMenu, { Commend3RowAction, Commend3RowActionItem } from "./RowActionsMenu";
import GenerationStatusBadge from "./GenerationStatusBadge";
import PromotionStatusBadge from "./PromotionStatusBadge";

type ModelsTableProps = {
  models: Commend3Model[];
  loading: boolean;
  sortField: Commend3ClientSortField;
  sortOrder: Commend3SortOrder;
  onSortChange: (field: Commend3ClientSortField, order: Commend3SortOrder) => void;
  selectedIds: string[];
  onToggleRow: (model: Commend3Model) => void;
  onOpenModel: (model: Commend3Model) => void;
  buildRowActions: (model: Commend3Model) => Commend3RowActionItem[];
  onRowAction: (model: Commend3Model, action: Commend3RowAction) => void;
  emptyContent?: React.ReactNode;
};

const HEAD_CELL =
  "tw-px-[13px] tw-py-[9px] tw-text-left tw-text-[10px] tw-font-semibold tw-uppercase tw-text-[#59596E]";
const BODY_CELL = "tw-px-[13px] tw-py-[11px]";
const HEAD_BG = "#F4F5F9";
const CELL_BORDER = "0.5px solid #E3E5EC";

const SortCaret = () => (
  <svg
    viewBox="0 0 24 24"
    className="tw-h-[11px] tw-w-[11px] tw-shrink-0"
    style={{
      stroke: "currentColor",
      fill: "none",
      strokeWidth: 2,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      opacity: 0.55,
    }}
    aria-hidden="true"
  >
    <path d="M8 9l4-4 4 4M8 15l4 4 4-4" />
  </svg>
);

const TypeIcon = ({ substitution }: { substitution: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    className="tw-h-[18px] tw-w-[18px]"
    style={{ stroke: "#0B0C23", fill: "none", strokeWidth: 1.4, strokeLinecap: "round" }}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9.2" />
    {substitution ? (
      <path d="M9 7.6v8.8M12 7.6v8.8M15 7.6v8.8" />
    ) : (
      <path d="M7.6 9h8.8M7.6 12h8.8M7.6 15h8.8" />
    )}
  </svg>
);

const ModelsTable = ({
  models,
  loading,
  sortField,
  sortOrder,
  onSortChange,
  selectedIds,
  onToggleRow,
  onOpenModel,
  buildRowActions,
  onRowAction,
  emptyContent,
}: ModelsTableProps) => {
  const handleSort = (field: Commend3ClientSortField) => {
    const nextOrder: Commend3SortOrder =
      sortField === field && sortOrder === "asc" ? "desc" : "asc";
    onSortChange(field, nextOrder);
  };

  const lastId = models.length ? models[models.length - 1].uid : null;

  const sortableHeader = (
    field: Commend3ClientSortField,
    label: string,
    extra: React.CSSProperties = {}
  ) => (
    <th
      className={`${HEAD_CELL} tw-cursor-pointer tw-select-none`}
      style={{ background: HEAD_BG, borderBottom: CELL_BORDER, letterSpacing: ".05em", ...extra }}
      onClick={() => handleSort(field)}
    >
      <span className="tw-inline-flex tw-items-center tw-gap-[5px]">
        {label}
        <SortCaret />
      </span>
    </th>
  );

  return (
    <div
      id="commend3-models-table"
      className="tw-rounded-xl tw-border-[0.5px] tw-border-[#E3E5EC] tw-bg-white"
    >
      <div
        data-hscroll="1"
        className="tw-overflow-x-auto"
        style={{ overflowY: "visible", borderRadius: "12px 12px 0 0" }}
      >
        <table
          className="tw-w-full tw-text-[13px]"
          style={{
            minWidth: MODELS_TABLE_MIN_WIDTH,
            borderCollapse: "separate",
            borderSpacing: 0,
            tableLayout: "fixed",
          }}
        >
          <thead>
            <tr style={{ background: HEAD_BG }}>
              <th
                className="tw-px-[13px] tw-py-[9px]"
                style={{
                  width: "34px",
                  minWidth: "34px",
                  background: HEAD_BG,
                  borderBottom: CELL_BORDER,
                  position: "sticky",
                  left: 0,
                  zIndex: 12,
                }}
              />
              {sortableHeader("name", "Model", {
                width: "230px",
                minWidth: "230px",
                position: "sticky",
                left: "34px",
                zIndex: 12,
              })}
              {sortableHeader("strategy", "Strategy Assigned", { width: "250px" })}
              {sortableHeader("generation", "Strategy Generation Status", {
                width: "200px",
              })}
              {sortableHeader("promotion", "Promotion Status", { width: "140px" })}
              {sortableHeader("widget", "Widget", { width: "100px" })}
              {sortableHeader("updated", "Last Updated", { width: "110px" })}
              {sortableHeader("by", "Updated By", { width: "110px" })}
              <th
                className="tw-px-[13px] tw-py-[9px]"
                style={{ width: "34px", background: HEAD_BG, borderBottom: CELL_BORDER }}
              />
            </tr>
          </thead>
          <tbody>
            {models.map((model) => (
              <tr key={model.uid}>
                <td
                  className={BODY_CELL}
                  style={{
                    borderBottom: CELL_BORDER,
                    position: "sticky",
                    left: 0,
                    zIndex: 6,
                    background: "#fff",
                  }}
                >
                  <input
                    type="checkbox"
                    className="tw-h-3.5 tw-w-3.5 tw-cursor-pointer"
                    style={{ accentColor: "#030562" }}
                    checked={selectedIds.includes(model.uid)}
                    onChange={() => onToggleRow(model)}
                    aria-label={`Select ${model.name}`}
                  />
                </td>
                <td
                  className={BODY_CELL}
                  style={{
                    borderBottom: CELL_BORDER,
                    position: "sticky",
                    left: "34px",
                    zIndex: 6,
                    background: "#fff",
                  }}
                >
                  <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
                    <div
                      className="tw-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center"
                      title={
                        model.type === "Substitution" ? "Substitution model" : "Co-selling model"
                      }
                    >
                      <TypeIcon substitution={model.type === "Substitution"} />
                    </div>
                    <button
                      type="button"
                      className="tw-truncate tw-text-left tw-text-[13px] tw-font-medium hover:tw-underline"
                      style={{ color: "#0066A0" }}
                      onClick={() => onOpenModel(model)}
                      title={model.name}
                    >
                      {model.name}
                    </button>
                  </div>
                </td>
                <td
                  className={`${BODY_CELL} tw-truncate tw-whitespace-nowrap`}
                  style={{ borderBottom: CELL_BORDER }}
                >
                  <span
                    className="tw-text-[12px]"
                    style={
                      hasStrategy(model)
                        ? { color: "#030562", fontWeight: 500 }
                        : { color: "#B4B9D4" }
                    }
                  >
                    {hasStrategy(model) ? getStrategyLabel(model) : NOT_CONFIGURED_LABEL}
                  </span>
                </td>
                <td
                  className={`${BODY_CELL} tw-whitespace-nowrap`}
                  style={{ borderBottom: CELL_BORDER }}
                >
                  <GenerationStatusBadge
                    id={`commend3-row-${model.uid}-generation`}
                    model={model}
                  />
                </td>
                <td
                  className={`${BODY_CELL} tw-whitespace-nowrap`}
                  style={{ borderBottom: CELL_BORDER, position: "relative" }}
                >
                  <PromotionStatusBadge
                    id={`commend3-row-${model.uid}-promotion`}
                    badge={resolvePromotionBadge(model)}
                    timestamp={formatTimestampToUTC(model.promotion_status?.promoted_at)}
                  />
                </td>
                <td
                  className={`${BODY_CELL} tw-truncate tw-whitespace-nowrap tw-text-[12px] tw-text-[#59596E]`}
                  style={{ borderBottom: CELL_BORDER }}
                >
                  {getWidgetLabel(model) ?? ""}
                </td>
                <td
                  className={`${BODY_CELL} tw-truncate tw-whitespace-nowrap tw-text-[12px] tw-text-[#59596E]`}
                  style={{ borderBottom: CELL_BORDER }}
                >
                  {formatDateOnly(model.updated_at) ?? ""}
                </td>
                <td
                  className={`${BODY_CELL} tw-truncate tw-whitespace-nowrap tw-text-[12px] tw-text-[#59596E]`}
                  style={{ borderBottom: CELL_BORDER }}
                >
                  {getUpdatedByName(model)}
                </td>
                <td
                  className={BODY_CELL}
                  style={{ borderBottom: CELL_BORDER, position: "relative" }}
                >
                  <RowActionsMenu
                    id={`commend3-row-${model.uid}-actions`}
                    actions={buildRowActions(model)}
                    openUpward={model.uid === lastId && models.length > 1}
                    onAction={(action) => onRowAction(model, action)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!loading && models.length === 0 && emptyContent ? (
        <div className="tw-px-4 tw-py-10">{emptyContent}</div>
      ) : null}
    </div>
  );
};

export default ModelsTable;
